import React, { FC, useEffect, useMemo, useState } from 'react';
import Select, { components, MultiValue, SingleValue } from 'react-select';
import { selectItemType } from '@src/types';
import { ArrowDownIcon } from '@src/svg';

export interface OptionType {
  value: string | number;
  label: string;
}

interface IOptions {
  countValue?: number;
  disabled: boolean;
  options: Array<selectItemType>;
  onChange?: (selected: selectItemType) => void;
  dataTestId?: string;
  removeCaret?: boolean;
}

const SizeChangeSelect: FC<IOptions> = ({
  countValue = 50,
  disabled,
  onChange,
  options,
  dataTestId,
  removeCaret = false,
}) => {
  const customComponents = useMemo(() => {
    const baseComponents: any = {
      Control: (props: any) => (
        <components.Control
          {...props}
          innerProps={{ ...props.innerProps, 'data-test-id': dataTestId }}
        />
      ),
      Input: (props: any) => (
        <components.Input {...props} data-test-id={`${dataTestId}-input`} />
      ),
      Option: (props: any) => (
        <components.Option
          {...props}
          innerProps={{
            ...props.innerProps,
            'data-test-id': `${dataTestId}-option-${props.data.id}`,
          }}
        />
      ),
      DropdownIndicator: (props: any) => (
        <components.DropdownIndicator {...props}>
          <ArrowDownIcon width={16} height={16} />
        </components.DropdownIndicator>
      ),
    };

    if (removeCaret || disabled) {
      baseComponents.DropdownIndicator = () => null;
      baseComponents.IndicatorSeparator = () => null;
    }

    return baseComponents;
  }, [removeCaret, disabled, dataTestId]);

  const defaultOption = useMemo(() => {
    if (disabled) return { id: 100, label: '0', value: '0' };
    return (
      options.find((option) => String(option.value) === String(countValue)) || {
        id: 99,
        label: String(countValue),
        value: String(countValue),
      }
    );
  }, [disabled, options, countValue]);

  const [selectedOption, setSelectedOption] =
    useState<selectItemType>(defaultOption);

  useEffect(() => {
    setSelectedOption(defaultOption);
  }, [defaultOption]);

  useEffect(() => {
    if (
      !selectedOption ||
      !options.some((opt) => opt.value === selectedOption.value)
    ) {
      setSelectedOption(defaultOption);
    }
  }, [defaultOption, options]);

  const handleChange = (
    selected: SingleValue<OptionType> | MultiValue<OptionType>
  ) => {
    if (selected) {
      setSelectedOption(selected as selectItemType);
      onChange && onChange(selected as selectItemType);
    }
  };

  return (
    <Select
      classNamePrefix="custom-select"
      styles={{
        control: (provided) => ({
          ...provided,
        }),
        option: (provided, state) => ({
          ...provided,
          backgroundColor:
            state.isFocused ||
            String(state.data.value) === String(selectedOption.value)
              ? 'rgba(109,49,237,0.22)'
              : 'white',
          fontSize: '12px',
          color: '#000',
          '&:hover': {
            backgroundColor: 'rgba(109,49,237,0.22)',
          },
        }),
        menuPortal: (provided) => ({
          ...provided,
          zIndex: 100001,
          minWidth: 78,
        }),
      }}
      isDisabled={disabled}
      isSearchable={false}
      value={selectedOption}
      onChange={handleChange}
      options={options}
      menuPlacement="auto"
      menuPosition="fixed"
      maxMenuHeight={190}
      menuPortalTarget={
        typeof document !== 'undefined' ? document.body : undefined
      }
      closeMenuOnScroll={() => true}
      noOptionsMessage={() => 'No options available'}
      components={customComponents}
    />
  );
};

export default SizeChangeSelect;
